import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { supabase } from './supabaseClient';
import './AdminChat.css';

const AdminChat = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const messagesEndRef = useRef(null);

  // Get admin info
  const adminEmail = localStorage.getItem('adminEmail') || 'admin@symbolfly.com';
  const adminName = adminEmail.split('@')[0] || 'Admin';

  // Fetch chat sessions with real-time updates
  useEffect(() => {
    fetchSessions();
    
    // Set up real-time subscription for sessions
    console.log('Setting up real-time subscription for chat sessions');
    const channel = supabase
      .channel('admin-chat-sessions', {
        config: {
          broadcast: { self: true }
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_sessions'
        },
        (payload) => {
          console.log('Session updated via real-time:', payload);
          fetchSessions();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages'
        },
        () => {
          // Refresh sessions when messages change (to update last_message_at)
          fetchSessions();
        }
      )
      .subscribe((status) => {
        console.log('Sessions subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to chat sessions');
        }
      });

    // Fallback: Poll for session updates every 5 seconds
    const pollInterval = setInterval(() => {
      fetchSessions();
    }, 5000);

    return () => {
      console.log('Cleaning up sessions subscription');
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };
  }, [statusFilter]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0 && selectedSession) {
      scrollToBottom();
    }
  }, [messages, selectedSession]);

  // Load messages when session is selected
  useEffect(() => {
    if (selectedSession) {
      loadMessages();
      
      // Subscribe to new messages for this session
      console.log('Setting up admin real-time subscription for session:', selectedSession.id);
      
      const channel = supabase
        .channel(`admin-chat-messages-${selectedSession.id}`, {
          config: {
            broadcast: { self: true }
          }
        })
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `session_id=eq.${selectedSession.id}`
          },
          (payload) => {
            console.log('Admin: New message received via real-time:', payload.new);
            // Only add if message doesn't already exist (avoid duplicates)
            setMessages((prev) => {
              const exists = prev.some(msg => msg.id === payload.new.id || (msg.id && msg.id.toString() === payload.new.id.toString()));
              if (exists) {
                console.log('Admin: Message already exists, skipping');
                return prev;
              }
              console.log('Admin: Adding new message to chat');
              const newMessages = [...prev, payload.new];
              // Scroll to bottom after state update
              setTimeout(() => {
                scrollToBottom();
              }, 50);
              return newMessages;
            });
            if (payload.new.sender_type === 'user') {
              markMessageAsRead(payload.new.id);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'chat_messages',
            filter: `session_id=eq.${selectedSession.id}`
          },
          (payload) => {
            console.log('Admin: Message updated via real-time:', payload.new);
            setMessages((prev) =>
              prev.map(msg => msg.id === payload.new.id ? payload.new : msg)
            );
          }
        )
        .subscribe((status) => {
          console.log('Admin subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('✅ Admin successfully subscribed to chat messages');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Admin error subscribing to chat messages');
          } else if (status === 'TIMED_OUT') {
            console.warn('⚠️ Admin subscription timed out, retrying...');
          } else if (status === 'CLOSED') {
            console.warn('⚠️ Admin subscription closed');
          }
        });

      // Fallback: Poll for new messages every 2 seconds if real-time fails
      const pollInterval = setInterval(async () => {
        try {
          const { data, error } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('session_id', selectedSession.id)
            .order('created_at', { ascending: true });

      if (!error && data) {
        setMessages((prev) => {
          // Only update if we have new messages
          if (data.length > prev.length) {
            console.log('Admin: Polling found new messages');
            // Scroll to bottom when new messages are found
            setTimeout(() => {
              scrollToBottom();
            }, 100);
            return data;
          }
          return prev;
        });
      }
        } catch (err) {
          console.error('Admin: Error polling messages:', err);
        }
      }, 2000);

      return () => {
        console.log('Admin: Cleaning up subscription for session:', selectedSession.id);
        clearInterval(pollInterval);
        supabase.removeChannel(channel);
      };
    }
  }, [selectedSession]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('chat_sessions')
        .select('*')
        .order('last_message_at', { ascending: false })
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSessions(data || []);

      // Auto-select first session if none selected
      if (!selectedSession && data && data.length > 0) {
        setSelectedSession(data[0]);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedSession) return;

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', selectedSession.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      
      // Mark all messages as read
      const unreadMessages = data?.filter(m => !m.is_read && m.sender_type === 'user') || [];
      if (unreadMessages.length > 0) {
        await supabase
          .from('chat_messages')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .in('id', unreadMessages.map(m => m.id));
      }
      
      // Scroll to bottom after messages are loaded
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const markMessageAsRead = async (messageId) => {
    try {
      await supabase
        .from('chat_messages')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', messageId);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedSession || sending) return;

    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately
    setSending(true);

    // Optimistic update - add message to UI immediately
    const tempMessage = {
      id: `temp-${Date.now()}`,
      session_id: selectedSession.id,
      message: messageText,
      sender_type: 'admin',
      sender_id: adminEmail,
      sender_name: adminName,
      is_read: true,
      created_at: new Date().toISOString()
    };
    setMessages((prev) => [...prev, tempMessage]);
    scrollToBottom();

    try {
      // Assign session to admin if not already assigned
      if (!selectedSession.assigned_to) {
        await supabase
          .from('chat_sessions')
          .update({ 
            assigned_to: adminEmail, // Now supports text (email) after schema update
            assigned_at: new Date().toISOString()
          })
          .eq('id', selectedSession.id);
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert([
          {
            session_id: selectedSession.id,
            message: messageText,
            sender_type: 'admin',
            sender_id: adminEmail, // Now supports text (email) after schema update
            sender_name: adminName,
            is_read: true
          }
        ])
        .select()
        .single();

      if (error) throw error;

      // Replace temp message with real message from database
      setMessages((prev) => 
        prev.map(msg => 
          msg.id === tempMessage.id ? data : msg
        )
      );
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove temp message on error
      setMessages((prev) => prev.filter(msg => msg.id !== tempMessage.id));
      setNewMessage(messageText); // Restore message text
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const closeSession = async (sessionId) => {
    try {
      await supabase
        .from('chat_sessions')
        .update({ 
          status: 'closed',
          closed_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      fetchSessions();
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error closing session:', error);
    }
  };

  const scrollToBottom = () => {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
      // Also try scrolling the messages container directly
      const messagesContainer = document.querySelector('.admin-chat-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getUnreadCount = (session) => {
    // This would need to be calculated from messages
    // For now, return 0 or implement a count query
    return 0;
  };

  return (
    <AdminLayout pageTitle="Live Chat">
      <div className="admin-chat-container">
        <div className="admin-chat-sidebar">
          <div className="admin-chat-sidebar-header">
            <h2>Chat Sessions</h2>
            <div className="admin-chat-filters">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-chat-filter-select"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="waiting">Waiting</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="admin-chat-sessions-list">
            {loading ? (
              <div className="admin-chat-loading">Loading sessions...</div>
            ) : sessions.length === 0 ? (
              <div className="admin-chat-empty">No chat sessions found</div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  className={`admin-chat-session-item ${
                    selectedSession?.id === session.id ? 'admin-chat-session-item-active' : ''
                  }`}
                  onClick={() => setSelectedSession(session)}
                >
                  <div className="admin-chat-session-header">
                    <div className="admin-chat-session-name">{session.user_name}</div>
                    <div className="admin-chat-session-status-badge">{session.status}</div>
                  </div>
                  <div className="admin-chat-session-info">
                    {session.user_email && (
                      <span className="admin-chat-session-email">{session.user_email}</span>
                    )}
                    {session.last_message_at && (
                      <span className="admin-chat-session-time">
                        {formatDate(session.last_message_at)}
                      </span>
                    )}
                  </div>
                  {session.message_count > 0 && (
                    <div className="admin-chat-session-meta">
                      {session.message_count} messages
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-chat-main">
          {selectedSession ? (
            <>
              <div className="admin-chat-header">
                <div className="admin-chat-header-info">
                  <h3>{selectedSession.user_name}</h3>
                  <div className="admin-chat-header-details">
                    {selectedSession.user_email && <span>{selectedSession.user_email}</span>}
                    {selectedSession.user_phone && <span>{selectedSession.user_phone}</span>}
                  </div>
                </div>
                <div className="admin-chat-header-actions">
                  {selectedSession.status === 'active' && (
                    <button
                      className="admin-chat-close-session-btn"
                      onClick={() => closeSession(selectedSession.id)}
                    >
                      Close Session
                    </button>
                  )}
                </div>
              </div>

              <div className="admin-chat-messages">
                {messages.map((message, index) => {
                  const showDate = index === 0 || 
                    new Date(message.created_at).toDateString() !== 
                    new Date(messages[index - 1].created_at).toDateString();
                  
                  return (
                    <React.Fragment key={message.id}>
                      {showDate && (
                        <div className="admin-chat-date-divider">
                          {formatDate(message.created_at)}
                        </div>
                      )}
                      <div
                        className={`admin-chat-message ${
                          message.sender_type === 'admin' 
                            ? 'admin-chat-message-admin' 
                            : 'admin-chat-message-user'
                        }`}
                      >
                        <div className="admin-chat-message-content">
                          <div className="admin-chat-message-text">{message.message}</div>
                          <div className="admin-chat-message-time">{formatTime(message.created_at)}</div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <form className="admin-chat-input-form" onSubmit={sendMessage}>
                <input
                  type="text"
                  className="admin-chat-input"
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  disabled={sending || selectedSession.status !== 'active'}
                />
                <button
                  type="submit"
                  className="admin-chat-send-button"
                  disabled={!newMessage.trim() || sending || selectedSession.status !== 'active'}
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </form>
            </>
          ) : (
            <div className="admin-chat-empty-main">
              <p>Select a chat session to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChat;
